import { track } from "@/utils/conversions";
import { PATHWAY_TO_AESTHETICS_EVENT_DETAIL_ID } from "@/utils/payment-plan";

export const CAREER_SPONSORSHIP_CAMPAIGN_SLUG =
  "career-sponsorship-fund-2026";

export const CAREER_SPONSORSHIP_PAGE_PATH =
  "/upgrade-your-career-with-cosmetic-college";

/** Applications open for September 2026 fund. */
export const CAREER_SPONSORSHIP_START = new Date("2026-09-06T00:00:00+01:00");
export const CAREER_SPONSORSHIP_END = new Date("2026-09-30T23:59:59+01:00");

export const CAREER_SPONSORSHIP_PERCENT = 30;

export const CAREER_SPONSORSHIP_EVENT_NAME =
  "2026 Career Sponsorship Fund";

export const CAREER_SPONSORSHIP_EYEBROW =
  "September 2026 Career Sponsorship";

export const CAREER_SPONSORSHIP_HEADLINE =
  "Why wait for next year? Train in 2026 and be ready for 2027";

export const CAREER_SPONSORSHIP_SUBHEAD =
  "Supporting a limited number of aspiring aesthetic professionals with up to 30% investment contributions towards selected professional training pathways and qualifications.";

export const CAREER_SPONSORSHIP_URGENCY =
  "Applications close 30 September - or earlier once the allocated sponsorship places have been awarded.";

export const CAREER_SPONSORSHIP_BANNER_HEADLINE =
  "SEPTEMBER 2026 CAREER SPONSORSHIP";

export const CAREER_SPONSORSHIP_BANNER_SUBHEAD =
  "Up to 30% towards selected qualifications - Limited places | Apply by 30 September";

export const CAREER_SPONSORSHIP_PAYMENT_NOTE =
  "Flexible payment available on totals of £3,000 or more - secure your place from 30% upfront plus a £250 admin fee, then spread the remaining balance over 6 months, interest-free.";

/** In-house payment plans only when the payable package total is this or more (inc. VAT). */
export const CAREER_SPONSORSHIP_PAYMENT_PLAN_MIN_INC_VAT = 3000;

export type WizardBackgroundId =
  | "beginner"
  | "beauty"
  | "cpd"
  | "regulated"
  | "healthcare"
  | "unsure";

export type WizardGoalId =
  | "own-business"
  | "employment"
  | "injectables"
  | "skin"
  | "laser"
  | "pmu";

export type WizardTimelineId =
  | "before-year-end"
  | "q1-2027"
  | "flexible";

/** Complete Aesthetics Professional Pathway (marketing hero / 3-7 package). */
export const PATHWAY_PACKAGE = {
  id: "complete-aesthetics-professional-pathway",
  eventDetailId: PATHWAY_TO_AESTHETICS_EVENT_DETAIL_ID,
  title: "Complete Aesthetics Professional Pathway",
  badge: "Hero pathway",
  description:
    "The complete regulated route from access foundation through to Level 7 injectables.",
  href: "/pathway-to-aesthetics/",
  priceIncVat: 11998.8,
  priceExVat: 9999,
  individualIncVat: 14047.8,
  paymentUpfrontIncVat: 3999.6,
  paymentMonthlyIncVat: 1333.2,
  paymentMonths: 6,
} as const;

export type SponsorshipCourseId =
  | "level-3-access"
  | "level-3-bls"
  | "level-4-skin"
  | "level-4-laser"
  | "level-4-hifu"
  | "level-4-dermaplaning"
  | "level-4-micropigmentation"
  | "level-4-microblading"
  | "level-5-skin"
  | "level-5-laser-tattoo"
  | "level-7-injectables";

export type SponsorshipCourse = {
  id: SponsorshipCourseId | string;
  title: string;
  href: string;
  contentfulId: string;
  /** Hasura event_detail.id when known - required for upcoming date lookups. */
  eventDetailId?: string;
};

/**
 * Course catalogue used to compose goal packages.
 * eventDetailId is required for sponsorship date unlocking; fill gaps as IDs are confirmed.
 */
export const SPONSORSHIP_COURSES: Record<
  SponsorshipCourseId,
  SponsorshipCourse
> = {
  "level-3-access": {
    id: "level-3-access",
    title: "VTCT Level 3 Certificate in Access to Aesthetic Therapies",
    href: "/courses/vtct/level-3-certificate-in-access-to-aesthetic-therapies/",
    contentfulId: "1graQlnkDnOBSl8sHkdf2L",
    eventDetailId: "80d59648-2fc7-4ea4-b268-029aaf47e6a4",
  },
  "level-3-bls": {
    id: "level-3-bls",
    title:
      "VTCT Level 3 Award in Adult Basic Life Support, AED and Management of Anaphylaxis",
    href: "/courses/vtct/level-3-award-in-basic-life-support-aed-and-management-of-anaphylaxis/",
    contentfulId: "5yvq5HxADjTDihwhE3fHsM",
  },
  "level-4-skin": {
    id: "level-4-skin",
    title:
      "VTCT Level 4 Certificate in Advanced Aesthetic Therapies for Skin Rejuvenation",
    href: "/courses/vtct/level-4-certificate-in-advanced-aesthetic-therapies-for-skin/",
    contentfulId: "4eE28e2XRAIhmKXO5UJGi",
    eventDetailId: "eb6789ac-84e9-4684-909c-0478ef047d21",
  },
  "level-4-laser": {
    id: "level-4-laser",
    title: "VTCT Level 4 Laser Hair Removal",
    href: "/courses/vtct/laser-hair-removal/",
    contentfulId: "5MrEz8xx8s0YFtNHRCes3i",
    eventDetailId: "ab170dd8-6132-4873-9392-f9cf87f114c0",
  },
  "level-4-hifu": {
    id: "level-4-hifu",
    title:
      "VTCT Level 4 Award in High Intensity Focused Ultrasound (HIFU)",
    href: "/courses/vtct/level-4-award-in-high-intensity-focused-ultrasound-hifu/",
    contentfulId: "6thZWz9oi1C1EJTWuoRVi1",
  },
  "level-4-dermaplaning": {
    id: "level-4-dermaplaning",
    title: "VTCT Level 4 Award in Dermaplaning",
    href: "/courses/vtct/level-4-award-in-dermaplaning/",
    contentfulId: "4bH4KENdeJVgfdZ7iNEwJg",
  },
  "level-4-micropigmentation": {
    id: "level-4-micropigmentation",
    title: "VTCT Level 4 Certificate in Micropigmentation",
    href: "/courses/vtct/level-4-certificate-in-micropigmentation/",
    contentfulId: "3C2fRaQRiwMjA3Inq6UHqK",
  },
  "level-4-microblading": {
    id: "level-4-microblading",
    title:
      "VTCT Level 4 Certificate in Enhancing Eyebrows with Microblading Techniques",
    href: "/courses/vtct/level-4-certificate-in-enhancing-eyebrows-with-microblading-techniques/",
    contentfulId: "2D6Xd7ARoNFR8qaqRl7FM2",
  },
  "level-5-skin": {
    id: "level-5-skin",
    title:
      "VTCT Level 5 Certificate in Non-surgical Aesthetic Procedures for Skin Rejuvenation",
    href: "/courses/vtct/level-5-certificate-in-non-surgical-aesthetic-procedures-for-skin/",
    contentfulId: "4LRrhhrfoDMXiu7pOORPov",
    eventDetailId: "80d5c0f9-9937-4e38-b1f6-8ba2b2b403e3",
  },
  "level-5-laser-tattoo": {
    id: "level-5-laser-tattoo",
    title: "VTCT Level 5 Certificate in Laser Tattoo Removal",
    href: "/courses/vtct/level-5-certificate-in-laser-tattoo-removal/",
    contentfulId: "6vB1wp7JL0TWzX46zfK0qw",
  },
  "level-7-injectables": {
    id: "level-7-injectables",
    title:
      "VTCT Level 7 Diploma in Non-surgical Aesthetic Injectable Procedures",
    href: "/courses/vtct/level-7-diploma-in-non-surgical-aesthetic-injectable-procedures/",
    contentfulId: "2Scklnr73aCDKTZ9nhfT7R",
    eventDetailId: "3eac27ac-57d4-4e5d-b50b-e5531c416288",
  },
};

export type GoalPackageId =
  | "complete-aesthetics-professional-pathway"
  | "employment-package"
  | "skin-professional-package"
  | "laser-professional-package"
  | "pmu-artist-package";

export type GoalPackage = {
  id: GoalPackageId;
  title: string;
  description: string;
  href: string;
  courseIds: readonly SponsorshipCourseId[];
  /** True when this maps to the priced Level 3-7 pathway package SKU. */
  isCompletePathway: boolean;
  priceIncVat?: number;
  packageEventDetailId?: string;
};

/** Full Level 3-7 pathway course set shared by own-business and injectables. */
export const COMPLETE_PATHWAY_COURSE_IDS = [
  "level-3-access",
  "level-3-bls",
  "level-4-skin",
  "level-5-skin",
  "level-7-injectables",
] as const satisfies readonly SponsorshipCourseId[];

export const GOAL_PACKAGES: Record<GoalPackageId, GoalPackage> = {
  "complete-aesthetics-professional-pathway": {
    id: "complete-aesthetics-professional-pathway",
    title: PATHWAY_PACKAGE.title,
    description: PATHWAY_PACKAGE.description,
    href: PATHWAY_PACKAGE.href,
    courseIds: COMPLETE_PATHWAY_COURSE_IDS,
    isCompletePathway: true,
    priceIncVat: PATHWAY_PACKAGE.priceIncVat,
    packageEventDetailId: PATHWAY_PACKAGE.eventDetailId,
  },
  "employment-package": {
    id: "employment-package",
    title: "Employment Boost Package",
    description:
      "Access foundation plus laser, advanced skin, HIFU and dermaplaning - built to strengthen clinic employability.",
    href: "/courses/vtct/",
    courseIds: [
      "level-3-access",
      "level-4-laser",
      "level-4-skin",
      "level-4-hifu",
      "level-4-dermaplaning",
    ],
    isCompletePathway: false,
  },
  "skin-professional-package": {
    id: "skin-professional-package",
    title: "Skin Professional Package",
    description:
      "Access foundation through Level 4 and Level 5 skin, with HIFU as a high-demand add-on.",
    href: "/courses/vtct/level-5-certificate-in-non-surgical-aesthetic-procedures-for-skin/",
    courseIds: [
      "level-3-access",
      "level-4-skin",
      "level-5-skin",
      "level-4-hifu",
    ],
    isCompletePathway: false,
  },
  "laser-professional-package": {
    id: "laser-professional-package",
    title: "Laser Professional Package",
    description:
      "Access foundation, Level 4 laser hair removal, and Level 5 laser tattoo removal.",
    href: "/courses/vtct/laser-hair-removal/",
    courseIds: ["level-3-access", "level-4-laser", "level-5-laser-tattoo"],
    isCompletePathway: false,
  },
  "pmu-artist-package": {
    id: "pmu-artist-package",
    title: "Permanent Makeup Artist Package",
    description:
      "Access foundation plus Level 4 micropigmentation and microblading qualifications.",
    href: "/courses/vtct/level-4-certificate-in-micropigmentation/",
    courseIds: [
      "level-3-access",
      "level-4-micropigmentation",
      "level-4-microblading",
    ],
    isCompletePathway: false,
  },
};

export const GOAL_TO_PACKAGE: Record<WizardGoalId, GoalPackageId> = {
  "own-business": "complete-aesthetics-professional-pathway",
  injectables: "complete-aesthetics-professional-pathway",
  employment: "employment-package",
  skin: "skin-professional-package",
  laser: "laser-professional-package",
  pmu: "pmu-artist-package",
};

export function getGoalPackage(goal: WizardGoalId | null): GoalPackage {
  const packageId = goal
    ? GOAL_TO_PACKAGE[goal]
    : "complete-aesthetics-professional-pathway";
  return GOAL_PACKAGES[packageId];
}

/** Provisional planner templates that omit Level 3 Access; this is not evidence of eligibility. */
export const BACKGROUNDS_SKIPPING_ACCESS = [
  "beauty",
  "regulated",
  "healthcare",
] as const satisfies readonly WizardBackgroundId[];

export function shouldSkipAccessCourse(
  background: WizardBackgroundId | null,
): boolean {
  if (!background) {
    return false;
  }

  return (BACKGROUNDS_SKIPPING_ACCESS as readonly string[]).includes(
    background,
  );
}

/** VTCT level used to enforce completion order (cannot take L4 before L3, etc.). */
export function getSponsorshipCourseLevel(
  courseId: string,
): number {
  if (courseId.startsWith("level-3")) return 3;
  if (courseId.startsWith("level-4")) return 4;
  if (courseId.startsWith("level-5")) return 5;
  if (courseId.startsWith("level-7")) return 7;
  return 99;
}

export function isLevel7SponsorshipCourse(course: {
  courseId?: string;
  id?: string;
  eventDetailId?: string;
  title?: string;
}): boolean {
  const id = course.courseId || course.id || "";
  if (id === "level-7-injectables" || id.startsWith("level-7")) {
    return true;
  }

  const level7DetailId = SPONSORSHIP_COURSES["level-7-injectables"].eventDetailId;
  if (level7DetailId && course.eventDetailId === level7DetailId) {
    return true;
  }

  return /level\s*7/i.test(course.title || "");
}

/** Resolve a known sponsorship course from a catalog event_detail id. */
export function findSponsorshipCourseByEventDetailId(
  eventDetailId: string,
): SponsorshipCourse | null {
  return (
    Object.values(SPONSORSHIP_COURSES).find(
      (course) => course.eventDetailId === eventDetailId,
    ) || null
  );
}

/** Package courses sorted by level, preserving package order within a level. */
export function getPackageCourses(
  goalPackage: GoalPackage,
  background: WizardBackgroundId | null = null,
): SponsorshipCourse[] {
  const skipAccess = shouldSkipAccessCourse(background);

  return [...goalPackage.courseIds]
    .filter((id) => !(skipAccess && id === "level-3-access"))
    .map((id) => SPONSORSHIP_COURSES[id])
    .sort((a, b) => {
      const levelDiff =
        getSponsorshipCourseLevel(a.id) - getSponsorshipCourseLevel(b.id);
      if (levelDiff !== 0) {
        return levelDiff;
      }
      return (
        goalPackage.courseIds.indexOf(a.id as SponsorshipCourseId) -
        goalPackage.courseIds.indexOf(b.id as SponsorshipCourseId)
      );
    });
}

export function getPackageEventDetailIds(
  goalPackage: GoalPackage,
  background: WizardBackgroundId | null = null,
): string[] {
  return getPackageCourses(goalPackage, background)
    .map((course) => course.eventDetailId)
    .filter((id): id is string => Boolean(id));
}

/** Stages shown on the hero pathway card. */
export const PATHWAY_PACKAGE_STAGES = COMPLETE_PATHWAY_COURSE_IDS.map((id) => {
  const course = SPONSORSHIP_COURSES[id];
  return {
    id: course.id,
    level: course.title.includes("Level 7")
      ? "Level 7"
      : course.title.includes("Level 5")
        ? "Level 5"
        : course.title.includes("Level 4")
          ? "Level 4"
          : "Level 3",
    title: course.title,
    href: course.href,
  };
});

/** Deep-link into the builder with the hero pathway ready to secure. */
export function buildCareerSponsorshipPathwaySecurePath() {
  const params = new URLSearchParams({
    goal: "own-business",
    background: "beginner",
    timeline: "flexible",
    builder: "secure",
  });
  return `${CAREER_SPONSORSHIP_PAGE_PATH}/?${params.toString()}#programme-builder`;
}

/** Deep-link into the builder with a goal selected, ready for background. */
export function buildCareerSponsorshipBuilderGoalPath(goal: WizardGoalId) {
  const params = new URLSearchParams({
    goal,
    builder: "background",
  });
  return `${CAREER_SPONSORSHIP_PAGE_PATH}/?${params.toString()}#programme-builder`;
}

export type CareerSponsorshipProgramme = {
  id: string;
  title: string;
  badge: string;
  description: string;
  href: string;
  courseId?: string;
  isHero?: boolean;
  priceIncVat?: number;
  sponsorshipEligible: boolean;
  goalPackageId: GoalPackageId;
  courses: SponsorshipCourse[];
};

export const WIZARD_BACKGROUNDS: ReadonlyArray<{
  id: WizardBackgroundId;
  label: string;
  hint: string;
}> = [
  {
    id: "beginner",
    label: "Completely new to aesthetics",
    hint: "No beauty or clinical training yet",
  },
  {
    id: "beauty",
    label: "Beauty therapist",
    hint: "Salon or spa experience",
  },
  {
    id: "cpd",
    label: "CPD / short-course trained",
    hint: "Looking to move onto regulated qualifications",
  },
  {
    id: "regulated",
    label: "Already hold a regulated aesthetics qualification",
    hint: "Level 3+ or equivalent",
  },
  {
    id: "healthcare",
    label: "Healthcare / medical background",
    hint: "Nurse, doctor, dentist or similar",
  },
  {
    id: "unsure",
    label: "Not sure where I fit",
    hint: "We will guide you",
  },
];

export const WIZARD_GOALS: ReadonlyArray<{
  id: WizardGoalId;
  label: string;
  hint: string;
}> = [
  {
    id: "own-business",
    label: "Start my own aesthetics business",
    hint: "Build a clinic-ready qualification pathway",
  },
  {
    id: "employment",
    label: "Boost my employment options",
    hint: "Get the credentials employers look for",
  },
  {
    id: "injectables",
    label: "Injectables professional",
    hint: "Focus on anti-wrinkle injections, fillers and clinical injectables",
  },
  {
    id: "skin",
    label: "Skin professional",
    hint: "Skin rejuvenation and advanced aesthetic therapies",
  },
  {
    id: "laser",
    label: "Laser professional",
    hint: "Laser hair removal and advanced laser routes",
  },
  {
    id: "pmu",
    label: "Permanent makeup artist",
    hint: "Brows, lips and micropigmentation specialisms",
  },
];

export const WIZARD_TIMELINES: ReadonlyArray<{
  id: WizardTimelineId;
  label: string;
  hint: string;
}> = [
  {
    id: "before-year-end",
    label: "As soon as dates work for me",
    hint: "Look for the next suitable course dates.",
  },
  {
    id: "q1-2027",
    label: "Planning further ahead",
    hint: "Explore options and discuss future intakes with admissions.",
  },
  {
    id: "flexible",
    label: "Flexible on timing",
    hint: "Choose dates that fit your plans.",
  },
];

export function isCareerSponsorshipActive(now = new Date()) {
  return now >= CAREER_SPONSORSHIP_START && now <= CAREER_SPONSORSHIP_END;
}

export function isCareerSponsorshipPromotionVisible(now = new Date()) {
  return isCareerSponsorshipActive(now);
}

export function formatSponsorshipGbp(amount: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

export function getSponsoredPriceIncVat(
  priceIncVat: number,
  percent = CAREER_SPONSORSHIP_PERCENT,
) {
  return Math.round(priceIncVat * (1 - percent / 100) * 100) / 100;
}

export function getSponsorshipContributionIncVat(
  priceIncVat: number,
  percent = CAREER_SPONSORSHIP_PERCENT,
) {
  return Math.round(priceIncVat * (percent / 100) * 100) / 100;
}

export type SponsorshipDateCandidate = {
  id: string;
  start: string;
  end?: string;
};

/**
 * Sliding sponsorship scale: each course is scored against the next available
 * date that still fits the package schedule (first selectable option after the
 * previous selected course). Selecting that next date on every course unlocks
 * the max percent. Choosing a later option on any course reduces the scale.
 */
export function getImmediacySponsorshipPercent({
  immediateCount,
  eligibleCount,
  maxPercent = CAREER_SPONSORSHIP_PERCENT,
}: {
  immediateCount: number;
  eligibleCount: number;
  maxPercent?: number;
}) {
  if (eligibleCount <= 0) {
    return 0;
  }

  const ratio = Math.min(Math.max(immediateCount, 0) / eligibleCount, 1);
  return Math.round(ratio * maxPercent);
}

export function getDateStartMs(date: SponsorshipDateCandidate) {
  return new Date(date.start).getTime();
}

export function getDateEndMs(date: SponsorshipDateCandidate) {
  return new Date(date.end || date.start).getTime();
}

export function getCourseDateEndIso(date: SponsorshipDateCandidate) {
  return date.end || date.start;
}

/** Calendar day key (yyyy-MM-dd) so same-day clashes are blocked. */
export function getDateDayKey(iso: string) {
  const date = new Date(iso);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Next course must start on a later calendar day than the previous course ends.
 * Same day is blocked - learner cannot attend two courses at once.
 */
export function isDateAfterPreviousCourse(
  candidate: SponsorshipDateCandidate,
  previousEndIso: string | null,
) {
  if (!previousEndIso) {
    return true;
  }

  return getDateDayKey(candidate.start) > getDateDayKey(previousEndIso);
}

export function filterDatesAfterPreviousCourse<
  T extends SponsorshipDateCandidate,
>(dates: T[], previousEndIso: string | null = null): T[] {
  const sorted = [...dates].sort(
    (a, b) => getDateStartMs(a) - getDateStartMs(b),
  );

  if (!previousEndIso) {
    return sorted;
  }

  return sorted.filter((date) =>
    isDateAfterPreviousCourse(date, previousEndIso),
  );
}

/** Absolute earliest listed intake for a course (before schedule constraints). */
export function findEarliestAvailableDate<T extends SponsorshipDateCandidate>(
  dates: T[],
): T | null {
  const sorted = [...dates].sort(
    (a, b) => getDateStartMs(a) - getDateStartMs(b),
  );
  return sorted[0] || null;
}

/** Next bookable date for a course after the previous course ends. */
export function findImmediateSequenceDate<T extends SponsorshipDateCandidate>(
  dates: T[],
  previousEndIso: string | null = null,
): T | null {
  return filterDatesAfterPreviousCourse(dates, previousEndIso)[0] || null;
}

export function scorePackageDateImmediacy<
  T extends SponsorshipDateCandidate & { id: string },
>(
  courses: ReadonlyArray<{
    dates: T[];
    selectedDateId: string | null;
  }>,
) {
  let previousEndIso: string | null = null;
  let immediateCount = 0;
  let eligibleCount = 0;
  const perCourse: Array<{
    immediateDateId: string | null;
    isImmediate: boolean;
    eligible: boolean;
    selectableDateIds: string[];
  }> = [];

  for (const course of courses) {
    const selectable = filterDatesAfterPreviousCourse(
      course.dates,
      previousEndIso,
    );
    const nextAvailable = selectable[0] || null;

    if (!nextAvailable) {
      perCourse.push({
        immediateDateId: null,
        isImmediate: false,
        eligible: false,
        selectableDateIds: [],
      });
      continue;
    }

    const selected =
      selectable.find((date) => date.id === course.selectedDateId) || null;
    const isImmediate = Boolean(selected && selected.id === nextAvailable.id);

    eligibleCount += 1;
    if (isImmediate) {
      immediateCount += 1;
    }

    perCourse.push({
      immediateDateId: nextAvailable.id,
      isImmediate,
      eligible: true,
      selectableDateIds: selectable.map((date) => date.id),
    });

    if (selected) {
      previousEndIso = getCourseDateEndIso(selected);
    } else {
      previousEndIso = getCourseDateEndIso(nextAvailable);
    }
  }

  const percent = getImmediacySponsorshipPercent({
    immediateCount,
    eligibleCount,
  });

  return {
    immediateCount,
    eligibleCount,
    percent,
    perCourse,
  };
}

export function getPathwaySponsoredPricing() {
  // Full pathway package floor: never discount below the published package price.
  const packageIncVat = PATHWAY_PACKAGE.priceIncVat;
  const upfront = PATHWAY_PACKAGE.paymentUpfrontIncVat;
  const monthly = PATHWAY_PACKAGE.paymentMonthlyIncVat;

  return {
    individualIncVat: PATHWAY_PACKAGE.individualIncVat,
    packageIncVat,
    /** Alias kept for existing call sites - always the package floor. */
    normalIncVat: packageIncVat,
    sponsoredIncVat: packageIncVat,
    contribution: 0,
    upfront,
    monthly,
    months: PATHWAY_PACKAGE.paymentMonths,
  };
}

export function recommendProgramme({
  background,
  goal,
}: {
  background: WizardBackgroundId | null;
  goal: WizardGoalId | null;
}): CareerSponsorshipProgramme {
  const goalPackage = getGoalPackage(goal);
  const courses = getPackageCourses(goalPackage, background);
  const includesAccess = courses.some(
    (course) => course.id === "level-3-access",
  );
  // Pathway package floor only applies when the full Access-inclusive pathway remains.
  const isHero = goalPackage.isCompletePathway && includesAccess;

  return {
    id: goalPackage.id,
    title: goalPackage.title,
    badge: isHero ? "Complete pathway" : "Goal package",
    description: shouldSkipAccessCourse(background)
      ? `${goalPackage.description} This starting plan omits Level 3 Access. Admissions must review your qualifications and confirm whether that course is needed.`
      : goalPackage.description,
    href: goalPackage.href,
    courseId: isHero ? goalPackage.packageEventDetailId : undefined,
    isHero,
    priceIncVat: isHero ? goalPackage.priceIncVat : undefined,
    sponsorshipEligible: true,
    goalPackageId: goalPackage.id,
    courses,
  };
}

export function buildCareerSponsorshipCallbackPath(
  options?: {
    source?: string;
    goal?: string | null;
    background?: string | null;
    timeline?: string | null;
    programmeId?: string | null;
  },
) {
  const search = new URLSearchParams();
  search.set("source", options?.source || CAREER_SPONSORSHIP_CAMPAIGN_SLUG);
  search.set("interest", "career_sponsorship");
  if (options?.goal) search.set("goal", options.goal);
  if (options?.background) search.set("background", options.background);
  if (options?.timeline) search.set("timeline", options.timeline);
  if (options?.programmeId) search.set("programme", options.programmeId);
  return `/request-a-callback/?${search.toString()}`;
}

export function buildCareerSponsorshipApplyPath(params?: {
  programmeId?: string;
  background?: string;
  goal?: string;
  timeline?: string;
}) {
  const search = new URLSearchParams();
  search.set("source", CAREER_SPONSORSHIP_CAMPAIGN_SLUG);
  search.set("interest", "career_sponsorship");
  if (params?.programmeId) search.set("programme", params.programmeId);
  if (params?.background) search.set("background", params.background);
  if (params?.goal) search.set("goal", params.goal);
  if (params?.timeline) search.set("timeline", params.timeline);
  return `/request-a-callback/?${search.toString()}`;
}

export function buildCareerSponsorshipQuizPath(
  campaign = CAREER_SPONSORSHIP_CAMPAIGN_SLUG,
) {
  const params = new URLSearchParams();
  params.set("campaign", campaign);
  return `/best-aesthetic-course-for-you/?${params.toString()}`;
}

export function isCareerSponsorshipCampaign(value?: string | string[] | null) {
  if (!value) {
    return false;
  }

  const testValue = Array.isArray(value) ? value[0] : value;
  return testValue === CAREER_SPONSORSHIP_CAMPAIGN_SLUG;
}

export function trackCareerSponsorshipEvent(
  action: string,
  params: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") {
    return;
  }

  track(action, {
    event_category: "Career Sponsorship Fund",
    event_label: CAREER_SPONSORSHIP_CAMPAIGN_SLUG,
    sponsorship_percent: CAREER_SPONSORSHIP_PERCENT,
    ...params,
  });
}

export function getCareerSponsorshipMetaDescription() {
  return `September 2026 Career Sponsorship Fund: up to ${CAREER_SPONSORSHIP_PERCENT}% towards selected VTCT qualifications and career pathways. Applications close 30 September or when places are awarded.`;
}

export function getDaysRemaining(now = new Date()) {
  const ms = CAREER_SPONSORSHIP_END.getTime() - now.getTime();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
